import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  User, ChevronDown, ChevronUp, FileText, PenTool, Download,
  CheckCircle, Eye, Upload, AlertTriangle, ClipboardCheck,
  TrendingUp, FileCheck, Calendar
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { getAuthPhotoUrl } from '../AuthImage';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { toast } from 'sonner';
import EPMSPConsultation from '../EPMSPConsultation';
import CompetencesPrerequisConsultation from '../CompetencesPrerequisConsultation';
import GrilleEvaluationReadOnly from '../GrilleEvaluationReadOnly';

const COLORS = [
  { border: 'border-l-[#8DC63F]', bg: 'bg-[#f7fdf0]', badge: 'bg-[#8DC63F]' },
  { border: 'border-l-[#3B82F6]', bg: 'bg-[#eff6ff]', badge: 'bg-[#3B82F6]' },
  { border: 'border-l-[#F59E0B]', bg: 'bg-[#fffbeb]', badge: 'bg-[#F59E0B]' },
  { border: 'border-l-[#EC4899]', bg: 'bg-[#fdf2f8]', badge: 'bg-[#EC4899]' },
  { border: 'border-l-[#8B5CF6]', bg: 'bg-[#f5f3ff]', badge: 'bg-[#8B5CF6]' },
];

const StagiaireAccordionCard = ({
  stagiaire, index, convention, unreadDocCount, newActivityCount,
  onSignConvention, onViewConvention, onDownloadConvention,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('profil');
  const [profile, setProfile] = useState(null);
  const [allDocs, setAllDocs] = useState([]);
  const [entDocs, setEntDocs] = useState([]);
  const [certData, setCertData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingEpmsp, setUploadingEpmsp] = useState(false);
  const [pendingAutonomie, setPendingAutonomie] = useState(0);
  const [epmspValidated, setEpmspValidated] = useState(false);
  const [hasDeclaration, setHasDeclaration] = useState(false);

  const color = COLORS[index % COLORS.length];
  const initials = `${(stagiaire.user_info?.prenom || '')[0] || ''}${(stagiaire.user_info?.nom || '')[0] || ''}`.toUpperCase();
  const stagiaireNom = `${stagiaire.user_info?.prenom || stagiaire.prenom || ''} ${stagiaire.user_info?.nom || stagiaire.nom || ''}`;
  const needsAutonomieValidation = stagiaire.statut_formation !== 'diplome' && !stagiaire.autonomie_validee;
  const needsConventionSignature = convention &&
    (convention.statut === 'en_attente_signatures' || convention.statut === 'partiellement_signe') &&
    !convention.signature_entreprise;
  const conventionValidated = convention && convention.statut === 'signe';

  useEffect(() => {
    if (isExpanded && !profile && !loading) {
      loadExpandedData();
    }
  }, [isExpanded]);

  useEffect(() => {
    loadPendingAutonomie();
    loadEpmspStatus();
  }, [stagiaire.user_id]);

  const loadPendingAutonomie = async () => {
    try {
      const res = await axios.get(`/autonomies/pending-for-stagiaire/${stagiaire.user_id}`);
      setPendingAutonomie(res.data?.count || 0);
    } catch (e) { setPendingAutonomie(0); }
  };

  const loadEpmspStatus = async () => {
    try {
      const [epmspRes, docsRes] = await Promise.all([
        axios.get(`/epmsp/stagiaire/${stagiaire.user_id}`).catch(() => ({ data: null })),
        axios.get(`/documents/${stagiaire.user_id}`).catch(() => ({ data: [] }))
      ]);
      setEpmspValidated(epmspRes.data?.all_validated || false);
      const docs = docsRes.data || [];
      setHasDeclaration(docs.some(d => d.document_type === 'declaration_epmsp' || d.type_document === 'declaration_epmsp'));
    } catch (e) { /* silent */ }
  };

  const loadExpandedData = async () => {
    setLoading(true);
    try {
      const [stagRes, docsRes, entDocsRes] = await Promise.all([
        axios.get(`/stagiaires/${stagiaire.user_id}`),
        axios.get(`/documents/${stagiaire.user_id}`),
        axios.get(`/entreprise/stagiaire/${stagiaire.user_id}/documents`).catch(() => ({ data: [] }))
      ]);
      setProfile(stagRes.data);
      setAllDocs(docsRes.data || []);
      setEntDocs(entDocsRes.data || []);

      try {
        const statusRes = await axios.get(`/epmsp/certificat-status/${stagiaire.user_id}`);
        if (statusRes.data.all_validated && statusRes.data.certificat_sent) {
          const docs = docsRes.data || [];
          setCertData({
            ...statusRes.data,
            declaration_doc: docs.find(d => d.document_type === 'declaration_epmsp'),
            attestation_doc: docs.find(d => d.document_type === 'attestation_epmsp'),
          });
        }
      } catch (e) {}
    } catch (e) {
    }
    setLoading(false);
  };

  const handleViewDoc = async (docId) => {
    try {
      const res = await axios.get(`/candidats/doc-view/${docId}`, { responseType: 'blob' });
      window.open(window.URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] })), '_blank');
    } catch (e) {
      try {
        const res = await axios.get(`/documents/download/${docId}`, { responseType: 'blob' });
        window.open(window.URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] })), '_blank');
      } catch (e) {
        toast.error('Erreur lors de la visualisation');
      }
    }
  };

  const handleDownloadDoc = async (docId, filename) => {
    try {
      const res = await axios.get(`/documents/download/${docId}`);
      const data = res.data;
      if (data.file_data) {
        const byteChars = atob(data.file_data);
        const byteNums = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
        const blob = new Blob([new Uint8Array(byteNums)], { type: data.content_type || 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename || 'document.pdf');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Document téléchargé');
      }
    } catch (e) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleUploadEpmsp = async (file) => {
    if (!file) return;
    setUploadingEpmsp(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_id', stagiaire.user_id);
      formData.append('document_type', 'declaration_epmsp');
      await axios.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Déclaration stagiaire uploadée !');
      const docsRes = await axios.get(`/documents/${stagiaire.user_id}`);
      setAllDocs(docsRes.data || []);
    } catch (e) {
      toast.error("Erreur lors de l'upload");
    }
    setUploadingEpmsp(false);
  };

  const epmspDoc = allDocs.find(d => d.document_type === 'declaration_epmsp' || d.type_document === 'declaration_epmsp');

  return (
    <div
      className={`${color.bg} border border-slate-200 ${color.border} border-l-4 rounded-lg shadow-sm transition-all hover:shadow-md`}
      data-testid={`stagiaire-card-${stagiaire.user_id}`}
    >
      {/* Card Header */}
      <div
        className="p-4 cursor-pointer select-none"
        onClick={() => setIsExpanded(!isExpanded)}
        data-testid={`stagiaire-header-${stagiaire.user_id}`}
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4 min-w-0">
            {(stagiaire.user_info?.photo_identite || stagiaire.user_info?.photo_url || stagiaire.photo_identite) ? (
              <img src={getAuthPhotoUrl(stagiaire.user_info?.photo_identite || stagiaire.user_info?.photo_url || stagiaire.photo_identite)}
                alt={stagiaireNom} className="w-11 h-11 rounded-full object-cover shadow-sm flex-shrink-0 border-2 border-white" data-testid={`stagiaire-photo-${stagiaire.user_id}`} />
            ) : (
              <div className={`w-11 h-11 ${color.badge} rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm flex-shrink-0`}>
                {initials}
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-[#0F172A] text-base">{stagiaireNom}</p>
                {needsConventionSignature && (
                  <Badge className="bg-orange-500 text-white text-[10px] px-1.5 py-0" data-testid={`badge-conv-${stagiaire.user_id}`}>Convention à signer</Badge>
                )}
                {conventionValidated && (
                  <Badge className="bg-green-600 text-white text-[10px] px-1.5 py-0" data-testid={`badge-conv-ok-${stagiaire.user_id}`}>Convention validée</Badge>
                )}
                {epmspValidated && (
                  <Badge className="bg-emerald-600 text-white text-[10px] px-1.5 py-0" data-testid={`badge-epmsp-ok-${stagiaire.user_id}`}>EPMSP validé</Badge>
                )}
                {hasDeclaration && (
                  <Badge className="bg-blue-600 text-white text-[10px] px-1.5 py-0" data-testid={`badge-declare-${stagiaire.user_id}`}>Stagiaire déclaré</Badge>
                )}
                {newActivityCount > 0 && !isExpanded && (
                  <span className="inline-flex items-center gap-1 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full animate-pulse" data-testid={`badge-activity-${stagiaire.user_id}`}>
                    {newActivityCount} nouveau{newActivityCount > 1 ? 'x' : ''}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500">{stagiaire.centre_formation} — {stagiaire.statut}</p>
              {stagiaire.type_contrat && (
                <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                  {stagiaire.type_contrat === 'stagiaire_fp' ? 'Stagiaire formation pro.' :
                   stagiaire.type_contrat === 'apprenti' ? 'Apprenti' :
                   stagiaire.type_contrat === 'contrat_pro' ? 'Contrat de professionnalisation' :
                   stagiaire.type_contrat === 'salarie' ? 'Salarié' : stagiaire.type_contrat}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
            <Link
              to={`/suivi-stagiaire?stagiaire=${stagiaire.user_id}`}
              className="px-3 py-1.5 bg-[#8DC63F] text-white rounded-lg text-sm font-medium hover:bg-[#7db336] transition-colors flex items-center gap-1"
              data-testid={`btn-notes-${stagiaire.user_id}`}
            >
              <TrendingUp className="h-4 w-4" /> Notes
            </Link>
            <Link
              to={`/validation-autonomie?stagiaire=${stagiaire.user_id}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 relative ${
                pendingAutonomie > 0
                  ? 'bg-orange-500 text-white hover:bg-orange-600'
                  : 'border border-[#8DC63F] text-[#8DC63F] hover:bg-[#8DC63F]/10'
              }`}
              data-testid={`btn-autonomie-${stagiaire.user_id}`}
            >
              <ClipboardCheck className="h-4 w-4" /> Valider l'autonomie
              {pendingAutonomie > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center animate-bounce">
                  {pendingAutonomie}
                </span>
              )}
            </Link>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 rounded-lg hover:bg-white/60 transition-colors"
              data-testid={`btn-expand-${stagiaire.user_id}`}
            >
              {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-500" /> : <ChevronDown className="h-5 w-5 text-slate-500" />}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Content with Tabs */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-slate-200/60">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8DC63F]" />
              <span className="ml-3 text-sm text-slate-500">Chargement...</span>
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-3">
              <TabsList className="w-full flex flex-wrap h-auto gap-1 bg-slate-100/80 p-1 rounded-lg">
                <TabsTrigger value="profil" className="flex-1 min-w-[90px] text-xs sm:text-sm gap-1" data-testid={`tab-profil-${stagiaire.user_id}`}>
                  <User className="h-3.5 w-3.5" /> Profil
                </TabsTrigger>
                <TabsTrigger value="convention" className="flex-1 min-w-[90px] text-xs sm:text-sm gap-1 relative" data-testid={`tab-convention-${stagiaire.user_id}`}>
                  <PenTool className="h-3.5 w-3.5" /> Convention
                  {needsConventionSignature && <span className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-orange-500 rounded-full" />}
                </TabsTrigger>
                <TabsTrigger value="competences" className="flex-1 min-w-[90px] text-xs sm:text-sm gap-1" data-testid={`tab-competences-${stagiaire.user_id}`}>
                  <CheckCircle className="h-3.5 w-3.5" /> Compétences
                </TabsTrigger>
                <TabsTrigger value="documents" className="flex-1 min-w-[90px] text-xs sm:text-sm gap-1 relative" data-testid={`tab-documents-${stagiaire.user_id}`}>
                  <FileText className="h-3.5 w-3.5" /> Documents
                  {unreadDocCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] rounded-full h-3.5 w-3.5 flex items-center justify-center font-bold">
                      {unreadDocCount}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* TAB: Profil */}
              <TabsContent value="profil" className="mt-3" data-testid={`content-profil-${stagiaire.user_id}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <h4 className="font-semibold text-[#0F172A] text-sm flex items-center gap-2">
                      <User className="h-4 w-4 text-green-600" /> Informations personnelles
                    </h4>
                    <div className="bg-white rounded-lg p-4 space-y-2 text-sm border border-slate-100">
                      <div className="flex gap-4">
                        {(profile?.photo_identite || stagiaire.photo_identite) && (
                          <img src={getAuthPhotoUrl(profile?.photo_identite || stagiaire.photo_identite)} alt="Photo" className="w-16 h-20 object-cover rounded border" />
                        )}
                        <div className="space-y-2 flex-1">
                          <p><span className="text-slate-500 w-28 inline-block">Email :</span> <span className="font-medium">{profile?.user_info?.email || profile?.email || '-'}</span></p>
                          <p><span className="text-slate-500 w-28 inline-block">Téléphone :</span> <span className="font-medium">{profile?.user_info?.telephone || profile?.telephone || '-'}</span></p>
                          <p><span className="text-slate-500 w-28 inline-block">Date naiss. :</span> <span className="font-medium">{profile?.date_naissance || '-'}</span></p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-red-50 rounded-lg border border-red-200 p-4 space-y-2 text-sm">
                      <h5 className="font-medium text-red-700 flex items-center gap-1 text-xs">
                        <AlertTriangle className="h-3 w-3" /> Contact d'urgence
                      </h5>
                      <p><span className="text-slate-500 w-28 inline-block">Nom :</span> <span className="font-medium">{profile?.contact_urgence_nom || '-'}</span></p>
                      <p><span className="text-slate-500 w-28 inline-block">Tél :</span> <span className="font-medium">{profile?.contact_urgence_telephone || '-'}</span></p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* TAB: Convention */}
              <TabsContent value="convention" className="mt-3" data-testid={`content-convention-${stagiaire.user_id}`}>
                {!convention ? (
                  <div className="bg-white rounded-lg border border-slate-200 p-6 text-center">
                    <PenTool className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">Aucune convention de mise en situation pédagogique en cours</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg border border-slate-200 p-4">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <p className="text-sm text-slate-600">
                          Formation : {convention.date_debut_formation} au {convention.date_fin_formation}
                        </p>
                        {convention.periodes_stage?.length > 0 && (
                          <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                            <Calendar className="h-3 w-3" /> {convention.periodes_stage.length} période(s) de stage
                          </p>
                        )}
                        <div className="mt-2">
                          <Badge
                            className={`${
                              convention.statut === 'signe' ? 'bg-green-100 text-green-700 border border-green-200' :
                              convention.statut === 'en_attente_validation_centre' ? 'bg-blue-100 text-blue-700 border border-blue-200' :
                              needsConventionSignature ? 'bg-orange-100 text-orange-700 border border-orange-200' :
                              'bg-amber-100 text-amber-700 border border-amber-200'
                            }`}
                            data-testid={`conv-status-${stagiaire.user_id}`}
                          >
                            {convention.statut === 'signe' ? 'Signée et validée' :
                             convention.statut === 'en_attente_validation_centre' ? 'En attente de validation du centre' :
                             needsConventionSignature ? 'En attente de votre signature' :
                             'Signatures en cours'}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button variant="outline" size="sm" onClick={() => (onViewConvention || onDownloadConvention)(stagiaire.user_id, `${stagiaire.prenom || ''}_${stagiaire.nom || ''}`)} data-testid={`btn-view-conv-${stagiaire.user_id}`}>
                          <Eye className="h-4 w-4 mr-1" /> Consulter
                        </Button>
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => onDownloadConvention(stagiaire.user_id, `${stagiaire.prenom || ''}_${stagiaire.nom || ''}`)} data-testid={`btn-download-conv-${stagiaire.user_id}`}>
                          <Download className="h-4 w-4 mr-1" /> Télécharger
                        </Button>
                        {needsConventionSignature && (
                          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white" onClick={() => onSignConvention(convention)} data-testid={`btn-sign-conv-${stagiaire.user_id}`}>
                            <PenTool className="h-4 w-4 mr-1" /> Signer
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* TAB: Compétences (Prérequis, Blocs — EPMSP masqué car déjà dans Documents) */}
              <TabsContent value="competences" className="mt-3" data-testid={`content-competences-${stagiaire.user_id}`}>
                <CompetencesPrerequisConsultation stagiaireId={stagiaire.user_id} hideEpmsp={true} />
              </TabsContent>

              {/* TAB: Documents */}
              <TabsContent value="documents" className="mt-3" data-testid={`content-documents-${stagiaire.user_id}`}>
                <div className="space-y-3">
                  {/* Documents EPMSP */}
                  {(() => {
                    const epmspAttestation = allDocs.find(d => d.document_type === 'attestation_epmsp' || d.type_document === 'attestation_epmsp');
                    const epmspDeclaration = allDocs.find(d => d.document_type === 'declaration_epmsp' || d.type_document === 'declaration_epmsp');
                    return (epmspAttestation || epmspDeclaration) ? (
                      <div className="bg-slate-50 rounded-lg border border-slate-200 p-3 space-y-2">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Documents EPMSP</p>
                        {epmspAttestation && (
                          <div className="flex items-center justify-between p-2 bg-white rounded border border-emerald-200">
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="h-4 w-4 text-emerald-600 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-800 truncate">{epmspAttestation.filename || epmspAttestation.nom || 'Attestation EPMSP'}</p>
                                {(epmspAttestation.uploaded_at || epmspAttestation.created_at) && <p className="text-xs text-slate-400">{new Date(epmspAttestation.uploaded_at || epmspAttestation.created_at).toLocaleDateString('fr-FR')}</p>}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <button onClick={() => handleViewDoc(epmspAttestation.document_id || epmspAttestation._id)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="Voir" data-testid={`view-epmsp-att-${stagiaire.user_id}`}>
                                <Eye className="h-4 w-4" />
                              </button>
                              <button onClick={() => handleDownloadDoc(epmspAttestation.document_id || epmspAttestation._id, epmspAttestation.filename || 'attestation_epmsp.pdf')} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="Télécharger" data-testid={`dl-epmsp-att-${stagiaire.user_id}`}>
                                <Download className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        )}
                        {epmspDeclaration && (
                          <div className="flex items-center justify-between p-2 bg-white rounded border border-emerald-200">
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="h-4 w-4 text-emerald-600 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-800 truncate">{epmspDeclaration.filename || epmspDeclaration.nom || 'Déclaration stagiaire'}</p>
                                {(epmspDeclaration.uploaded_at || epmspDeclaration.created_at) && <p className="text-xs text-slate-400">{new Date(epmspDeclaration.uploaded_at || epmspDeclaration.created_at).toLocaleDateString('fr-FR')}</p>}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <button onClick={() => handleViewDoc(epmspDeclaration.document_id || epmspDeclaration._id)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="Voir" data-testid={`view-epmsp-decl-${stagiaire.user_id}`}>
                                <Eye className="h-4 w-4" />
                              </button>
                              <button onClick={() => handleDownloadDoc(epmspDeclaration.document_id || epmspDeclaration._id, epmspDeclaration.filename || 'declaration_epmsp.pdf')} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="Télécharger" data-testid={`dl-epmsp-decl-${stagiaire.user_id}`}>
                                <Download className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : null;
                  })()}

                  {!epmspDoc && (
                    <div className="bg-white rounded-lg border border-dashed border-orange-300 p-3">
                      <p className="text-sm text-orange-700">Déclaration stagiaire en attente (à déposer par le stagiaire)</p>
                    </div>
                  )}

                  {entDocs.length > 0 ? (
                    entDocs.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-100">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="h-4 w-4 text-blue-500 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{doc.nom || doc.filename || doc.document_type || 'Document'}</p>
                            {doc.created_at && <p className="text-xs text-slate-400">{new Date(doc.created_at).toLocaleDateString('fr-FR')}</p>}
                          </div>
                        </div>
                        {(doc.url || doc.file_path) && (
                          <a href={doc.url || doc.file_path} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                            <Download className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">Aucun document partagé par le centre</p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </div>
      )}
    </div>
  );
};

export default StagiaireAccordionCard;
